import { memo, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import {ThemeMode } from 'config';

// project imports
import MenuList from '../MenuList';
import LogoSection from '../LogoSection';
import MiniDrawerStyled from './MiniDrawerStyled';

import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { drawerWidth } from 'store/constant';
import {IconChevronRight} from '@tabler/icons-react';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import headerPattern from 'assets/images/header-pattern_org.svg';

import { useEffect } from 'react';
// ==============================|| SIDEBAR DRAWER ||============================== //
function Sidebar() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const { menuOrientation, miniDrawer, mode } = useConfig();

  // Mirror the real drawer state onto <body>, not just manual toggles: pages such as the
  // floor plan editor collapse the sidebar programmatically via handlerDrawerOpen, and the
  // `.common-drawer-width` rules size every shared drawer off this class. Without the sync
  // a drawer opened from those pages keeps the 290 px expanded-sidebar offset while the
  // sidebar is actually 72 px wide.
  useEffect(() => {
    if (!isMdUp) {
      document.body.classList.remove('left-menu-drawer-open');
      document.body.classList.remove('left-menu-drawer-mini');
      return;
    }
    document.body.classList.toggle('left-menu-drawer-open', drawerOpen);
    document.body.classList.toggle('left-menu-drawer-mini', !drawerOpen);
  }, [isMdUp, drawerOpen]);

  const logo = (
    <Box
      sx={{
        display: 'flex',
        pl: 2,
        py: 0,
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor:
          theme.palette.mode === ThemeMode.DARK
            ? theme.palette.dark[900]
            : theme.palette.primary.main
      }}
    >
      <LogoSection />
      <img src={headerPattern} alt="header-pattern" height="48px" />
    </Box>
  );

  const drawer = useMemo(() => {
    const isVerticalOpen = menuOrientation === MenuOrientation.VERTICAL && drawerOpen;

    // The body classes follow from drawerOpen in the effect above, so the toggle only
    // has to move the state.
    const handleDrawerToggle = () => {
      handlerDrawerOpen(!drawerOpen);
    };

    let drawerSX = { paddingLeft: '0px', paddingRight: '0px', marginTop: '20px' };
    if (drawerOpen) drawerSX = { paddingLeft: '16px', paddingRight: '16px', marginTop: '0px' };

    return (
      <>
        <Box 
          sx={{ ...drawerSX, overscrollBehavior: 'contain', overflow: 'auto', height: 'calc(100vh - 88px)', '&::-webkit-scrollbar': {
            width: '5px',
          }, }}
          onWheel={(e) => {
            const el = e.currentTarget;
            const isAtTop = el.scrollTop === 0;
            const isAtBottom = el.scrollHeight - el.scrollTop === el.clientHeight;
            if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
              e.preventDefault();
            }
          }}
        >
          {/* <OrganizationDropdown drawerOpen={drawerOpen}/> */}
          <MenuList />
        </Box>
        <Box
          onClick={handleDrawerToggle}
          id="drawer-toggle-button"
          sx={{
            border: 'solid 1px',
            borderColor: 'grey.500',
            borderTopLeftRadius: '7px',
            borderBottomLeftRadius: '7px',
            width: 22,
            alignSelf: 'end',
            cursor: 'pointer',
            margin: '10px 0 60px 0',
            boxShadow: theme.palette.mode === ThemeMode.DARK ? 'none' : '0 2px 2px rgba(0, 0, 0, 0.10)',
            display: { xs: 'none', md: 'flex'}

          }}>
          <IconChevronRight stroke={1.5} size="20px"
            style={{
              position: 'relative',
              left: drawerOpen ? '0px' : '2px',
              transform: drawerOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }} />
        </Box>
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downMD, drawerOpen, menuOrientation, mode]);

  return (
    <Box component="nav" sx={{ flexShrink: { md: 0 }, width: { xs: 'auto', md: drawerWidth } }} aria-label="mailbox folders">
      {downMD || (miniDrawer && drawerOpen) ? (
        <Drawer
          variant={downMD ? 'temporary' : 'persistent'}
          anchor="left"
          open={drawerOpen}
          onClose={() => handlerDrawerOpen(!drawerOpen)}
          sx={{
            '& .MuiDrawer-paper': {
              mt: downMD ? 0 : 11,
              zIndex: 1099,
              width: drawerWidth,
              bgcolor: 'background.default',
              color: 'text.primary',
              borderRight: 'none',
              overscrollBehavior: 'contain'
            }
          }}
          ModalProps={{ keepMounted: true }}
          color="inherit"
        >
          {downMD && logo}
          {drawer}
        </Drawer>
      ) : (
        <MiniDrawerStyled variant="permanent" open={drawerOpen}>
          {logo}
          {drawer}
        </MiniDrawerStyled>
      )}
    </Box>
  );
}

export default memo(Sidebar);
